import {
    Box,
    Text,
    Anchor,
    Avatar,
    IconCheckbox,
    Image,
    MediaObject,
    Popover,
    Popper,
} from '@zillow/constellation';
import { debounce } from 'lodash';
import React, { useState, useEffect } from 'react';

import type { Contributor } from '../../B2CPostTemplate.types';
import ContributorPopover from '../ContributorPopover/ContributorPopover';

export interface ContributorsCardProps {
    author: Contributor;
    reviewers?: Contributor[];
    editors?: Contributor[];
}

const ContributorsCard: React.FC<ContributorsCardProps> = ({
    author,
    reviewers = [],
    editors = [],
}) => {
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth < 890);
        };
        const debouncedOnResize = debounce(onResize, 100);

        setIsMobile(window.innerWidth < 890);
        // eslint-disable-next-line no-undef
        window.addEventListener('resize', debouncedOnResize);
        window.addEventListener('orientationchange', debouncedOnResize);

        return () => {
            // eslint-disable-next-line no-undef
            window.removeEventListener('resize', debouncedOnResize);
            window.removeEventListener('orientationchange', debouncedOnResize);
        };
    }, []);

    // Helper to render name with popover
    const renderName = (contributor: Contributor, id: string) => {
        if (isMobile) {
            if (!contributor.link) {
                return contributor.name;
            }
            return (
                <Anchor id={id} href={contributor.link} target="_blank">
                    {contributor.name}
                </Anchor>
            );
        }
        return (
            <Popper triggered={popover(contributor)}>
                <Anchor id={id} onClick={(e) => e.preventDefault()}>
                    {contributor.name}
                </Anchor>
            </Popper>
        );
    };

    const popover = (contributor: Contributor) => (
        <Popover body={<ContributorPopover contributor={contributor} />} />
    );

    return (
        <>
            {/* Author with MediaObject */}
            <MediaObject
                media={
                    author.avatar ? (
                        <Avatar size="lg">
                            <Image src={author.avatar} alt={author.name} />
                        </Avatar>
                    ) : null
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '$spacing.xs',
                        justifyContent: 'center',
                    }}
                >
                    {/* Author */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '$spacing.xs',
                        }}
                    >
                        <Text fontType="body" fontColor="textDark">
                            Written by
                        </Text>
                        <Text as="span" fontType="body" fontColor="textDark">
                            {renderName(author, `author-${author.name}`)}
                        </Text>
                        {author.date && (
                            <>
                                <Text
                                    as="span"
                                    sx={{
                                        color: '$colors.coolGray500',
                                        margin: '0 $spacing.xs',
                                    }}
                                >
                                    |
                                </Text>
                                <Text fontType="body" fontColor="textDark">
                                    {author.date}
                                </Text>
                            </>
                        )}
                    </Box>
                    {/* Reviewers/Editors */}
                    {(!!reviewers.length || !!editors.length) && (
                        <MediaObject
                            gutter="xxs"
                            media={
                                <IconCheckbox
                                    fontColor="green500"
                                    size="sm"
                                    marginTop="NONE"
                                />
                            }
                        >
                            <Text fontType="body" fontColor="textDark">
                                {!!reviewers.length && (
                                    <>
                                        Reviewed by{' '}
                                        {reviewers.map((r, i) => (
                                            <React.Fragment key={r.name}>
                                                {i > 0 && ' and '}
                                                {renderName(
                                                    r,
                                                    `reviewer-${r.name}`
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                                {!!reviewers.length && !!editors.length && ', '}
                                {!!editors.length && (
                                    <>
                                        Edited by{' '}
                                        {editors.map((e, i) => (
                                            <React.Fragment key={e.name}>
                                                {i > 0 && ' and '}
                                                {renderName(
                                                    e,
                                                    `editor-${e.name}`
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </Text>
                        </MediaObject>
                    )}
                </Box>
            </MediaObject>
        </>
    );
};

export default ContributorsCard;
